import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { login } from 'api/auth.service';
import { getBroadcasts } from 'api/broadcasts.service';
import { getTasks } from 'api/task.service';
import { ADMIN, GUEST, SUPER_ADMIN } from 'common';
import { useToast } from 'hooks/ui';
import { REVOKED_STATUS, User } from 'models/user.model';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFavoritesStore } from 'store/favorites';
import { taskKeys } from './tasks/queryKeys';
import { broadcastKeys } from './broadcasts/queryKeys';
import { setCurrentUser } from 'store/user';

const LOGIN_ERROR_MESSAGE = "You've been logged out. Please log back in.";
const PERFORMANCE_RELOAD_TYPE = 'reload';

export const useAdminGetCurrent = () => {
  const navigate = useNavigate();
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const toast = useToast();

  const generateWarningMessage = (firstName: string): string =>
    `Hi, ${firstName}! Seems like you don't have an access here. Please contact your local super admin. `;

  const generateSuccesMessage = (firstName: string): string => {
    const isFirstTime = localStorage.getItem('first-time');

    if (!isFirstTime) {
      localStorage.setItem('first-time', 'true');

      return `Welcome, ${firstName}!`;
    }

    return `Welcome back, ${firstName}!`;
  };

  const isGuestUserOrRevoked = (data: User) => {
    const isGuestRole = data.role === GUEST;
    const isRevoked = data.status === REVOKED_STATUS;

    return isGuestRole || isRevoked;
  };

  const isNotPerformanceReloadType = () => {
    const windowPerformanceType = (
      window.performance.getEntries()[0] as PerformanceNavigationTiming
    ).type;

    return windowPerformanceType !== PERFORMANCE_RELOAD_TYPE;
  };

  const {
    isLoading,
    data: user,
    error,
  } = useQuery({
    queryKey: ['auth'],
    queryFn: login,
    retry: 1,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

  useLayoutEffect(() => {
    useFavoritesStore.getState().setUserId(user?.id ?? null);
  }, [user?.id]);

  const isFavouritesEnabled =
    !!user?.id && (user.role === ADMIN || user.role === SUPER_ADMIN);

  const { data: favouriteTasks } = useQuery({
    queryKey: taskKeys.favourites,
    queryFn: () => getTasks({ favourites: true }),
    enabled: isFavouritesEnabled,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const { data: favouriteBroadcasts } = useQuery({
    queryKey: broadcastKeys.favourites,
    queryFn: () => getBroadcasts({ favourites: true }),
    enabled: isFavouritesEnabled,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!isFavouritesEnabled || !favouriteTasks || !favouriteBroadcasts) return;

    useFavoritesStore.getState().replaceFavouritesFromServer(
      favouriteTasks.map((t) => t.id),
      favouriteBroadcasts.map((b) => b.id),
    );
  }, [isFavouritesEnabled, favouriteTasks, favouriteBroadcasts]);

  useEffect(() => {
    if (user) {
      setCurrentUser(user);

      if (initialLoad) {
        if (isGuestUserOrRevoked(user)) {
          toast.warning(generateWarningMessage(user.firstName));
        } else if (isNotPerformanceReloadType()) {
          toast.success(generateSuccesMessage(user.firstName));
        }

        setInitialLoad(false);
      }
    }
  }, [user, toast, initialLoad]);

  useEffect(() => {
    if (error) {
      setCurrentUser(null);
      if (error) {
        toast.error(LOGIN_ERROR_MESSAGE);
      }
      navigate('/login');
    }
  }, [error, toast, navigate]);

  return { isLoading, user };
};
